import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Alert, Spinner, Badge } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

// Interface for Business data
interface Business {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
}

const BusinessList: React.FC = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/businesses/');
        setBusinesses(response.data);
      } catch (error) {
        console.error('Error fetching businesses:', error);
        setError('Failed to load businesses');
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}

      {businesses.length === 0 && !error && !loading && (
         <p style={{
           textAlign: 'center',
           color: COLORS.text.secondary,
           paddingTop: SPACING[16],
           paddingBottom: SPACING[16],
           fontSize: TYPOGRAPHY.size.base,
           fontFamily: TYPOGRAPHY.body.fontFamily
         }}>No businesses found.</p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16]
      }}>
        {businesses.map((business) => (
          <div
            key={business.id}
            onClick={() => navigate(`/businesses/${business.id}`)}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[16],
              cursor: 'pointer',
              transition: 'box-shadow 150ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: SPACING[12]
            }}>
               <div style={{
                 padding: SPACING[8],
                 backgroundColor: `${COLORS.accent.default}10`,
                 borderRadius: '9999px'
               }}>
                 <BuildingOffice2Icon style={{
                   width: '20px',
                   height: '20px',
                   color: COLORS.accent.default
                 }} />
               </div>
               <div style={{ flex: 1 }}>
                   <h3 style={{
                     fontSize: TYPOGRAPHY.size.xl,
                     fontWeight: TYPOGRAPHY.weight.semibold,
                     color: COLORS.text.primary,
                     fontFamily: TYPOGRAPHY.headings.fontFamily,
                     margin: 0
                   }}>{business.name}</h3>
                   {business.description && (
                     <p style={{
                       marginTop: SPACING[4],
                       fontSize: TYPOGRAPHY.size.sm,
                       color: COLORS.text.secondary,
                       fontFamily: TYPOGRAPHY.body.fontFamily,
                       display: '-webkit-box',
                       WebkitLineClamp: 2,
                       WebkitBoxOrient: 'vertical',
                       overflow: 'hidden'
                     }}>{business.description}</p>
                   )}
                   {business.is_active !== undefined && (
                     <div style={{ marginTop: SPACING[8] }}>
                       <Badge variant={business.is_active ? 'success' : 'danger'} size="sm">
                         {business.is_active ? 'Active' : 'Inactive'}
                       </Badge>
                     </div>
                   )}
               </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BusinessList;
